/**
 * Everything the projects screens compute rather than render.
 *
 * React-free on purpose: the roadmap's columns are derived from dates rather
 * than stored, which is the one piece of this section that can be wrong in a way
 * a screenshot will not show, so it has to be readable and testable without
 * mounting anything.
 *
 * ── THE `et-EE` IN `fmtHours` IS THE POLICY, NOT AN OVERSIGHT ──────────────
 *
 * It has now been read twice as a hardcoded locale waiting to be parameterised,
 * so the reason is written down here rather than left to be rediscovered.
 *
 * `crm/ui/format.ts` is where this decision lives, and its header states it:
 * every number shape in the CRM - counts, money, and the hours below - goes
 * through the Estonian locale rather than through the interface language,
 * because the operator reads these figures beside bank statements and register
 * extracts. A separator that moved with the UI language would make one figure
 * look like two, and the person comparing them has no way to tell which copy is
 * which. `money` further down this file follows the same rule for the same
 * reason and says so at its own definition.
 *
 * So `fmtHours` is not out of step with `format.ts`; it IS `format.ts`'s policy,
 * applied to the one shape that module does not own. Threading a locale through
 * it would put this section out of step with every other CRM screen while
 * looking, in a diff, like a fix.
 *
 * What would have to change first for it to become a defect: a real operator who
 * does not read Estonian number formatting - a genuinely non-Estonian user of
 * the CRM, not a translated interface. At that point the change is not this line
 * but `format.ts` gaining an operator-level number locale that every shape in it
 * reads, and this function following it along with the rest. Changing one
 * formatter ahead of that produces a screen where the hours and the euros beside
 * them disagree about what a decimal comma is, which is worse than either
 * setting on its own.
 */
import type { Milestone, Project, Task } from "../../../lib/projects";
// Straight from the format module rather than the `ui` barrel: this file stays
// React-free so the column derivation below can be tested without a renderer.
import { fmtMoney } from "../../ui/format";

/**
 * Roadmap (a kanban derived from dates).
 *
 * The columns are not the ProjectStatus enum 1:1: not_started splits by start
 * date into "Planned" (scheduled, within FUTURE_HORIZON_DAYS) and "Future" (no
 * date, or further out than that). cancelled is left off the roadmap entirely.
 */
export const FUTURE_HORIZON_DAYS = 90;

export type RoadmapCol = "completed" | "in_progress" | "planned" | "future";

/** Left to right, the order the board draws its columns in. */
export const ROADMAP_ORDER: RoadmapCol[] = ["completed", "in_progress", "planned", "future"];

export function roadmapColumn(p: Project): RoadmapCol | null {
  if (p.status === "cancelled") return null;
  if (p.status === "completed") return "completed";
  if (p.status === "in_progress" || p.status === "on_hold") return "in_progress";
  // not_started: sorted by start date
  if (!p.startDate) return "future";
  const days = (new Date(p.startDate).getTime() - Date.now()) / 86_400_000;
  return days > FUTURE_HORIZON_DAYS ? "future" : "planned";
}

/** What dropping a card in a column writes back to the record. */
export function roadmapPatch(col: RoadmapCol): Record<string, unknown> {
  switch (col) {
    case "completed": return { status: "completed" };
    case "in_progress": return { status: "in_progress" };
    case "planned": return { status: "not_started", startDate: new Date().toISOString() };
    case "future": return { status: "not_started", startDate: null };
  }
}

/**
 * The in-project roadmap: tasks and milestones land in the same columns.
 *
 * A milestone has no "in progress" state, so an unfinished one is placed by its
 * due date: "Planned" or "Future" (an overdue milestone stays in Planned - it is
 * late, not further away).
 */
export function dueWithinHorizon(iso: string): boolean {
  return (new Date(iso).getTime() - Date.now()) / 86_400_000 <= FUTURE_HORIZON_DAYS;
}

export function taskColumn(tk: Task): RoadmapCol {
  if (tk.status === "done") return "completed";
  if (tk.status === "in_progress" || tk.status === "review") return "in_progress";
  if (!tk.dueDate) return "future";
  return dueWithinHorizon(tk.dueDate) ? "planned" : "future";
}

export function milestoneColumn(m: Milestone): RoadmapCol {
  if (m.done) return "completed";
  if (!m.dueDate) return "future";
  return dueWithinHorizon(m.dueDate) ? "planned" : "future";
}

/** A task whose due date has passed and which nobody has finished. */
export function isOverdue(tk: Task): boolean {
  return !!tk.dueDate && tk.status !== "done" && new Date(tk.dueDate) < new Date();
}

/**
 * Formatters for the currencies `fmtMoney` cannot express, built once each.
 *
 * `crm/ui/format.ts` holds two prebuilt Intl instances, both EUR by
 * construction, so a figure in another currency has nowhere to go through it.
 * Rather than widen a module every CRM screen imports, the extra case is built
 * here and EUR still delegates - one implementation for the currency the
 * operator actually reads next to a bank statement, and a second path only for
 * the rows that would otherwise be relabelled into euros. The long-term home is
 * `fmtMoney(cents, exact, currency)` itself; moving it there is a separate,
 * repo-wide change and not this ring's.
 *
 * The locale stays `et-EE` for the same reason format.ts gives: the operator
 * compares these figures against register extracts, and a separator that moved
 * with the interface language would make one figure look like two.
 */
const CURRENCY_FMT = new Map<string, Intl.NumberFormat>();

function currencyFormat(currency: string, exact: boolean): Intl.NumberFormat {
  const key = `${currency}:${exact}`;
  let fmt = CURRENCY_FMT.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat("et-EE", {
      style: "currency",
      currency,
      ...(exact ? { minimumFractionDigits: 2 } : { maximumFractionDigits: 0 }),
    });
    CURRENCY_FMT.set(key, fmt);
  }
  return fmt;
}

/** Intl throws on a malformed currency code, and a thrown formatter takes the
 *  whole tab down. Anything that is not three letters is not a currency. */
const ISO_CURRENCY = /^[A-Za-z]{3}$/;

/**
 * Money that may not exist. A project with no budget shows a dash, not "0 EUR":
 * zero is a budget somebody set, absent is a decision nobody has made yet.
 * `exact` keeps the cents, for rates and other figures that are not round.
 *
 * `currency` is the THIRD parameter and defaults to EUR on purpose. Every caller
 * that passes `exact` passes it positionally, so a currency inserted ahead of it
 * would silently turn `money(x, true)` into a currency of `true` - and the KPI
 * strip, which passes neither, would keep working while every rate on the page
 * printed nonsense. A row that carries its own currency (an invoice, an expense)
 * passes it; everything else says nothing and gets euros, which is what the
 * column default in the CRM schema says too.
 */
export function money(cents: number | null | undefined, exact = false, currency = "EUR"): string {
  if (cents == null) return "-";
  if (currency === "EUR" || !ISO_CURRENCY.test(currency)) return fmtMoney(cents, exact);
  return currencyFormat(currency, exact).format(cents / 100);
}

/** `<input type="date">` value to the ISO instant the API takes. */
export function toIso(v: string): string | undefined {
  return v ? new Date(v + "T00:00:00").toISOString() : undefined;
}

/** Minutes as hours, the way a timesheet reads them. The locale is deliberate
 *  and shared with every other figure in the CRM - see the file header before
 *  parameterising it. */
export function fmtHours(min: number): string {
  return (min / 60).toLocaleString("et-EE", { maximumFractionDigits: 2 });
}

/**
 * An ISO instant as the `yyyy-mm-dd` a calendar field wants, in LOCAL time.
 *
 * The inverse of `toIso` above, and it has to be built from the local parts
 * rather than sliced off the ISO string: `"2026-08-21T22:30:00Z"` sliced gives
 * the 21st, which is yesterday to anybody east of UTC who logged that hour at
 * half past one in the morning. Every entry written after the local day has
 * turned over would open its edit dialog on the wrong day, and saving would
 * then move it there.
 */
export function toDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Minutes as the hours a form field speaks, to two decimals.
 *
 * The round trip is exact, which is the property the edit dialogs depend on:
 * two decimals of an hour is 0.6 min of resolution, so `Math.round(h * 60)`
 * recovers the stored figure and an untouched field produces no change to send.
 *
 * Here rather than in the three dialogs that need it. It was already copied
 * twice - the time entry editor and the timer's stop dialog - and a third copy
 * was about to be written for logging time on a task. Two forms that round
 * differently would disagree about what an untouched field means, and the one
 * that thinks it changed sends a PATCH nobody asked for.
 */
export function toHoursInput(minutes: number): string {
  return String(Math.round((minutes / 60) * 100) / 100);
}
